import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { isBot } from '@/lib/utils/bot';
import { hasAnalyticsConsent } from '@/lib/utils/cookie-consent';
import { isCartPage, isFunnelPage } from '@/lib/rum-funnel';
import settings from '@/settings';

const parseSampleRate = (raw: string | undefined, fallback: number) => {
  const rate = Number(raw);

  return Number.isFinite(rate) && rate >= 0 && rate <= 100 ? rate : fallback;
};

// Percent of sessions tracked, set at build time via DD_RUM_SESSION_SAMPLE_RATE
// (see scripts/env.js). 0 is the kill switch. Core Web Vitals percentiles hold
// up on a small sample, so this stays low to keep RUM session cost flat.
const SESSION_SAMPLE_RATE = parseSampleRate(
  process.env.NEXT_PUBLIC_DD_RUM_SESSION_SAMPLE_RATE,
  0.1,
);

// Sessions that start on the cart, checkout or thank-you pages keep the higher
// rate (and the replays) they had before site-wide RUM: that funnel is where
// we need the detail. Applied when a session starts.
const FUNNEL_SESSION_SAMPLE_RATE = 5;
const FUNNEL_REPLAY_SAMPLE_RATE = 5;

// A Datadog client token is public (it ships in the client bundle), but it is
// still configuration: Next.js inlines NEXT_PUBLIC_* values at build time.
const RUM_CLIENT_TOKEN = process.env.NEXT_PUBLIC_DATADOG_RUM_CLIENT_TOKEN;

const REPLAY_SAMPLED_KEY = 'rum_replay_sampled';

// Replay is recorded only on funnel pages, for a fixed share of tracked
// sessions. The roll is kept per browser session so reloads don't re-roll it.
let replayRecording = false;
let replaySampledInMemory: boolean | undefined;

const isReplaySampled = () => {
  try {
    const stored = window.sessionStorage.getItem(REPLAY_SAMPLED_KEY);

    if (stored !== null) {
      return stored === '1';
    }

    const sampled = Math.random() * 100 < FUNNEL_REPLAY_SAMPLE_RATE;

    window.sessionStorage.setItem(REPLAY_SAMPLED_KEY, sampled ? '1' : '0');

    return sampled;
  } catch {
    replaySampledInMemory ??= Math.random() * 100 < FUNNEL_REPLAY_SAMPLE_RATE;

    return replaySampledInMemory;
  }
};

// Start recording when a sampled session enters the funnel and stop when it
// leaves, so replay never covers non-funnel pages.
const syncReplay = (pathname: string) => {
  const shouldRecord = isFunnelPage(pathname) && isReplaySampled();

  if (shouldRecord && !replayRecording) {
    replayRecording = true;
    datadogRum.startSessionReplayRecording({ force: true });
  } else if (!shouldRecord && replayRecording) {
    replayRecording = false;
    datadogRum.stopSessionReplayRecording();
  }
};

// RUM view names use the Next route template (e.g. /products/[handle]) rather
// than the URL, so p75 vitals group by template and never by product.
const viewNameFor = (routeTemplate: string) => routeTemplate || '/';

const isInitialized = () => Boolean(datadogRum.getInitConfiguration());

// Views are keyed on the URL path, not the template: /products/a and
// /products/b share a template but are separate page views. Shallow checkout
// steps change only the query, so they keep the same path and the same view.
let lastViewPath: string | undefined;

// Mirror the cookie-consent choice onto an already-running RUM client: RUM
// stays initialised across client-side navigation, so a revoked choice has to
// stop it explicitly. A no-op before init.
export const syncRumConsent = () => {
  if (!isInitialized()) {
    return;
  }

  datadogRum.setTrackingConsent(
    hasAnalyticsConsent() ? 'granted' : 'not-granted',
  );
};

// Call on every route change with router.pathname (the template).
export const trackRumView = (routeTemplate: string) => {
  if (!isInitialized()) {
    return;
  }

  syncRumConsent();

  if (!hasAnalyticsConsent()) {
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  const name = viewNameFor(routeTemplate);
  const { pathname } = window.location;
  const isNewView = pathname !== lastViewPath;

  if (isNewView) {
    lastViewPath = pathname;

    // With manual views RUM only starts at the first startView, which names
    // the initial-load view (no extra view); later calls are route changes.
    datadogRum.startView({ name });
  }
  datadogRum.setGlobalContextProperty('template', name);

  syncReplay(pathname);

  // Once per view, so shallow checkout step changes don't repeat it.
  if (isNewView && !isCartPage(pathname) && isFunnelPage(pathname)) {
    datadogRum.addAction('checkout_page_loaded', {
      page: 'checkout',
      timestamp: new Date().toISOString(),
    });
  }
};

const TRACED_ORIGINS = [
  /https:\/\/(.+\.)?reebelo\.com/,
  /https:\/\/(.+\.)?reebelo\.com\.au/,
  /https:\/\/(.+\.)?reebelo\.ca/,
  /https:\/\/(.+\.)?reebelo\.co\.nz/,
  /https:\/\/(.+\.)?reebelo\.sg/,
  /https:\/\/(.+\.)?reebelo\.blue/,
];

// Resources, long tasks and auto-collected clicks are only kept for the
// funnel: elsewhere RUM exists for Core Web Vitals, and these events are noise.
const keepEvent = (event: RumEvent) => {
  const detail =
    event.type === 'resource' ||
    event.type === 'long_task' ||
    (event.type === 'action' && event.action.type !== 'custom');

  if (!detail) {
    return true;
  }

  try {
    return isFunnelPage(new URL(event.view.url).pathname);
  } catch {
    return true;
  }
};

export const initDatadogRum = (routeTemplate = '') => {
  if (typeof window === 'undefined' || isInitialized()) {
    return;
  }

  // Don't initialize for bots
  if (isBot()) {
    return;
  }

  if (!hasAnalyticsConsent()) {
    return;
  }

  if (!RUM_CLIENT_TOKEN) {
    return;
  }

  const startsInFunnel = isFunnelPage(window.location.pathname);
  // The configured rate of 0 is a kill switch, funnel pages included.
  const sessionSampleRate =
    startsInFunnel && SESSION_SAMPLE_RATE > 0
      ? Math.max(SESSION_SAMPLE_RATE, FUNNEL_SESSION_SAMPLE_RATE)
      : SESSION_SAMPLE_RATE;

  if (sessionSampleRate <= 0) {
    return;
  }

  datadogRum.init({
    applicationId: '7ad78da8-cfad-4dfa-acba-344b917828db',
    clientToken: RUM_CLIENT_TOKEN,
    site: 'us3.datadoghq.com',
    // Matches the APM service and the sourcemap upload (--service=storefront)
    service: 'storefront',
    env: process.env.NEXT_PUBLIC_STAGE || 'development',
    version: process.env.NEXT_PUBLIC_APP_VERSION || 'dev',
    sessionSampleRate,
    // Every tracked session is replay-eligible but recording is started and
    // stopped by hand (syncReplay), so only sampled funnel visits are billed.
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
    // Views are started per route template from the router (trackRumView).
    trackViewsManually: true,
    // The SDK can't toggle these once running, and a session that starts on a
    // product page can still reach checkout, so collect always and drop the
    // detail outside the funnel in beforeSend.
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    beforeSend: keepEvent,
    defaultPrivacyLevel: 'mask-user-input',
    // Trace headers add CORS preflights on cross-origin calls, so only link
    // RUM to APM for requests made from funnel pages.
    allowedTracingUrls: [
      (url: string) =>
        TRACED_ORIGINS.some((origin) => origin.test(url)) &&
        isFunnelPage(window.location.pathname),
    ],
  });

  // Dimensions for slicing vitals. Low cardinality only: no URLs, ids or users.
  datadogRum.setGlobalContextProperty('store', settings.store);

  trackRumView(routeTemplate || window.location.pathname);
};

// Helper function to track checkout events
export const trackCheckoutEvent = (
  eventName: string,
  attributes?: Record<string, any>,
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const isCheckoutPage = window.location.pathname.includes('/checkout/');

  if (isCheckoutPage) {
    datadogRum.addAction(eventName, {
      page: 'checkout',
      timestamp: new Date().toISOString(),
      ...attributes,
    });
  }
};

// Helper function to track shipping tab events
export const trackShippingEvent = (
  eventName: string,
  attributes?: Record<string, any>,
) => {
  trackCheckoutEvent(eventName, {
    tab: 'shipping',
    ...attributes,
  });
};

// Helper function to track payment tab events
export const trackPaymentEvent = (
  eventName: string,
  attributes?: Record<string, any>,
) => {
  trackCheckoutEvent(eventName, {
    tab: 'payment',
    ...attributes,
  });
};

// Helpers to track cart page events. Unlike the checkout helpers these only
// fire once RUM has been initialised for the session.
export const trackCartEvent = (
  eventName: string,
  attributes?: Record<string, unknown>,
) => {
  if (typeof window === 'undefined' || !datadogRum.getInitConfiguration()) {
    return;
  }

  datadogRum.addAction(eventName, {
    page: 'cart',
    timestamp: new Date().toISOString(),
    ...attributes,
  });
};

// Reports a cart failure as a RUM error so it shows up in Error Tracking
export const trackCartError = (
  name: string,
  attributes?: Record<string, unknown>,
  cause?: unknown,
) => {
  if (typeof window === 'undefined' || !datadogRum.getInitConfiguration()) {
    return;
  }

  const error = new Error(name);

  error.name = name;

  datadogRum.addError(error, {
    page: 'cart',
    cause: cause instanceof Error ? cause.message : cause,
    ...attributes,
  });
};
